/**
 * Marketing attribution forwarding for the SPA (plan §4.8).
 *
 * Exists to carry one attribution bundle across the Clerk redirect: the public
 * site decorates its acquisition links with the nine keys, the app captures
 * them off the landing URL, and organization creation posts them back. Changes
 * when the attribution wire contract changes.
 *
 * **The SPA interprets nothing.** It reads nine known keys, stores them, and
 * posts them back — no parsing, no normalization, no Google anything. Length
 * caps, charset classes and the drop-never-fail rule all live in the backend's
 * `parseAttributionBundle`; duplicating any of them here would create a second
 * place for the contract to drift.
 *
 * Three properties are load-bearing:
 *
 * 1. **`sessionStorage`, not `localStorage`.** The bundle describes *this*
 *    arrival. A three-week-old campaign attached to an unrelated organization
 *    is worse than no attribution, because it looks like data.
 * 2. **First write wins.** Clerk's hop lands the user on
 *    `/app/select-organization` with no query params at all, and every in-app
 *    navigation after it is equally bare — none of them may erase the arrival
 *    that carried the bundle.
 * 3. **Cleared after a successful organization creation** (plan §8 B4), so a
 *    bundle can never attach to a second organization.
 *
 * The bundle holds no secret — the same values were visible in the URL bar —
 * but every storage access is still guarded: a hardened browser throws on
 * `sessionStorage`, and losing attribution must never break organization
 * creation.
 */

import {
	MARKETING_ATTRIBUTION_FIELDS,
	type MarketingAttribution,
} from "@packages/shared-types";

/** `sessionStorage` key for the bundle carried across the Clerk redirect. */
export const ATTRIBUTION_STORAGE_KEY = "ruralops-attribution";

/**
 * `sessionStorage`, or `undefined` where it is unavailable.
 *
 * Accessing the property itself throws in some hardened configurations, which
 * is why this is a function behind a try rather than a module-level constant.
 */
function storage(): Storage | undefined {
	try {
		return typeof window === "undefined" ? undefined : window.sessionStorage;
	} catch {
		return undefined;
	}
}

/**
 * Read the nine known keys off a query string. Pure, and the only place the
 * URL is looked at.
 *
 * An empty value is dropped: `?utm_source=` is the absence of a source, not a
 * source named "".
 */
function readFromSearch(search: string): MarketingAttribution {
	const params = new URLSearchParams(search);
	const bundle: MarketingAttribution = {};
	for (const field of MARKETING_ATTRIBUTION_FIELDS) {
		const value = params.get(field);
		if (value) bundle[field] = value;
	}
	return bundle;
}

/**
 * Capture the bundle off the landing URL into `sessionStorage`. **First write
 * wins**, so a later navigation carrying no params cannot erase a real bundle.
 *
 * A URL with no attribution params writes nothing at all, which leaves a later
 * genuine landing in the same tab free to win.
 */
export function captureAttributionFromUrl(search: string): void {
	const bundle = readFromSearch(search);
	if (Object.keys(bundle).length === 0) return;

	const store = storage();
	if (!store) return;
	try {
		if (store.getItem(ATTRIBUTION_STORAGE_KEY) !== null) return;
		store.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(bundle));
	} catch {
		// Storage blocked (private mode, hardened browser): this signup is
		// attributed as direct rather than failing.
	}
}

/**
 * Read back what `captureAttributionFromUrl` stored, or `undefined` when there
 * is nothing to send.
 *
 * `undefined` rather than `{}` so the caller can spread it straight onto the
 * create-organization payload: the route's `attribution` field is optional, and
 * an empty object would write a row of nulls for unattributed traffic.
 *
 * Only the nine known keys survive the read — anything else a tab's storage
 * happens to hold under this key is ignored rather than forwarded.
 */
export function readStoredAttribution(): MarketingAttribution | undefined {
	const store = storage();
	if (!store) return undefined;

	let raw: string | null;
	try {
		raw = store.getItem(ATTRIBUTION_STORAGE_KEY);
	} catch {
		return undefined;
	}
	if (!raw) return undefined;

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return undefined;
	}
	if (typeof parsed !== "object" || parsed === null) return undefined;

	const stored = parsed as Record<string, unknown>;
	const bundle: MarketingAttribution = {};
	for (const field of MARKETING_ATTRIBUTION_FIELDS) {
		const value = stored[field];
		if (typeof value === "string" && value) bundle[field] = value;
	}
	return Object.keys(bundle).length === 0 ? undefined : bundle;
}

/**
 * Drop the stored bundle.
 *
 * Called once the organization it belongs to exists, so the next organization
 * this tab creates is attributed on its own merits or not at all.
 */
export function clearStoredAttribution(): void {
	const store = storage();
	if (!store) return;
	try {
		store.removeItem(ATTRIBUTION_STORAGE_KEY);
	} catch {
		// Nothing to do: the bundle is per-tab and dies with the tab anyway.
	}
}
